import { ThemeEvents, CartAddEvent } from '@theme/events';
import { fetchConfig } from '@theme/utilities';

/**
 * Matching set offer (blocks/matching-set.liquid, snippets/matching-set-panel.liquid).
 * - Shows the panel whose data-colour matches the colour the shopper picks.
 * - Adds the partner variant to the cart without leaving the page and opens the cart drawer.
 * Without JavaScript the panel forms post to /cart/add as normal.
 */
class MatchingSetOffer extends HTMLElement {
  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {HTMLElement | null} */
  #productInformation = null;

  connectedCallback() {
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    this.addEventListener('submit', this.#onSubmit, { signal });

    this.#productInformation = this.closest('[id*="ProductInformation-"]');
    this.#productInformation?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });
  }

  disconnectedCallback() {
    this.#abortController?.abort();
  }

  /**
   * @param {Event & { detail?: { resource?: { options?: string[] } } }} event
   */
  #onVariantUpdate = (event) => {
    const colourIndex = Number.parseInt(this.dataset.colourIndex ?? '-1', 10);
    const colour = event.detail?.resource?.options?.[colourIndex];
    if (colourIndex < 0 || !colour) return;

    const panels = this.querySelectorAll('.matching-set__panel');
    const hasMatch = Array.from(panels).some((panel) => panel instanceof HTMLElement && panel.dataset.colour === colour);
    if (!hasMatch) return;

    for (const panel of panels) {
      if (panel instanceof HTMLElement) panel.hidden = panel.dataset.colour !== colour;
    }
  };

  /**
   * @param {SubmitEvent} event
   */
  #onSubmit = async (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.classList.contains('matching-set__form')) return;

    event.preventDefault();

    const button = form.querySelector('button[type="submit"]');
    button?.setAttribute('aria-busy', 'true');

    const formData = new FormData(form);
    const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
      .map((element) => (element instanceof HTMLElement ? element.dataset.sectionId : undefined))
      .filter(Boolean);
    if (sectionIds.length) formData.append('sections', sectionIds.join(','));

    try {
      const fetchCfg = fetchConfig('javascript', { body: formData });
      const response = await fetch(Theme.routes.cart_add_url, {
        ...fetchCfg,
        headers: { ...fetchCfg.headers, Accept: 'text/html' },
      });
      const data = await response.json();

      if (data.status) {
        // Cart error (e.g. sold out): fall back to the normal form post so the shopper sees the message.
        form.submit();
        return;
      }

      const cart = await fetch(`${Theme.routes.cart_url}.js`).then((cartResponse) => cartResponse.json());

      document.dispatchEvent(
        new CartAddEvent(cart, String(formData.get('id')), {
          source: 'matching-set',
          itemCount: 1,
          sections: data.sections,
        })
      );
    } catch (error) {
      console.error(error);
      form.submit();
    } finally {
      button?.removeAttribute('aria-busy');
    }
  };
}

if (!customElements.get('matching-set-offer')) {
  customElements.define('matching-set-offer', MatchingSetOffer);
}
